import { useState, useEffect } from 'react';
import { useAuth } from '@/hooks/useAuth';
import { useNavigate } from 'react-router-dom';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Heart, Lock, Mail } from 'lucide-react';
import axios from 'axios';
import { API } from '@/App';

export default function LoginPage() {
  const { login, isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [settings, setSettings] = useState({});

  // Redirect if already authenticated
  useEffect(() => {
    if (isAuthenticated) navigate('/admin', { replace: true });
  }, [isAuthenticated, navigate]);

  useEffect(() => {
    axios.get(`${API}/settings?lite=true`).then(r => setSettings(r.data || {})).catch(() => {});
  }, []);

  const salonName = settings.salon_name || 'B∞kd';
  const salonLogo = settings.icon_logo_url || settings.logo_url || '';

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      const success = await login(email, password);
      if (success) {
        navigate('/admin', { replace: true });
      } else {
        setError('Invalid email or password');
      }
    } catch (err) {
      setError(err.response?.data?.detail || 'Invalid email or password');
    }
    setLoading(false);
  };

  return (
    <div className="min-h-screen bg-background flex items-center justify-center p-4">
      <div className="w-full max-w-sm">
        {/* Logo */}
        <div className="text-center mb-8">
          {salonLogo ? (
            <img
              src={salonLogo}
              alt={salonName}
              className="w-20 h-20 rounded-2xl mx-auto mb-4 shadow-lg object-cover"
            />
          ) : (
            <div className="w-20 h-20 rounded-2xl mx-auto mb-4 shadow-lg bg-primary/10 flex items-center justify-center text-primary text-2xl font-bold">
              {((salonName || 'B')[0]).toUpperCase()}
            </div>
          )}
          <h1 className="text-2xl font-semibold tracking-tight" style={{ fontFamily: 'Playfair Display' }}>{salonName}</h1>
          <p className="text-sm text-muted-foreground mt-1">Admin Dashboard</p>
        </div>

        <Card className="p-6 rounded-2xl border shadow-[0_10px_30px_rgba(43,18,27,0.08)]">
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <Label htmlFor="email" className="text-xs">Email</Label>
              <div className="relative mt-1">
                <Mail size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
                <Input 
                  id="email" 
                  type="email" 
                  value={email} 
                  onChange={e => setEmail(e.target.value)} 
                  placeholder="your@email.com" 
                  className="pl-9 rounded-xl"
                  data-testid="login-email"
                  required 
                />
              </div>
            </div>
            <div>
              <Label htmlFor="password" className="text-xs">Password</Label>
              <div className="relative mt-1">
                <Lock size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
                <Input 
                  id="password" 
                  type="password" 
                  value={password} 
                  onChange={e => setPassword(e.target.value)} 
                  placeholder="Enter password" 
                  className="pl-9 rounded-xl"
                  data-testid="login-password"
                  required 
                />
              </div>
            </div>
            {error && (
              <p className="text-xs text-destructive font-medium">{error}</p>
            )}
            <Button 
              type="submit" 
              disabled={loading} 
              className="w-full rounded-xl h-11"
              data-testid="login-submit"
            >
              {loading ? 'Signing in...' : 'Sign in'}
            </Button>
          </form>
        </Card>

        <p className="text-center text-xs text-muted-foreground mt-6">
          <Heart size={10} className="inline mr-1" />
          Powered by B∞kd
        </p>
      </div>
    </div>
  );
}
